import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ProviderWithProducts } from "../../../../hooks/useProviders";
import { useApp } from "../../../../contexts/AppContext";
import { supabase } from "../../../../lib/supabase";

interface Props {
  provider: ProviderWithProducts;
}

export default function ProviderHeroSection({ provider }: Props) {
  const { currentUser, isLoggedIn, toggleFollow, followedProviders } = useApp();
  const [favored, setFavored] = useState(false);
  const [followCount, setFollowCount] = useState(provider.follower_count ?? 0);
  const [followLoading, setFollowLoading] = useState(false);

  const isFollowing = followedProviders.has(provider.id);

  useEffect(() => {
    if (!isLoggedIn || !currentUser) return;
    supabase
      .from("purchases")
      .select("id", { count: "exact", head: true })
      .eq("buyer_id", currentUser.id)
      .eq("provider_id", provider.id)
      .then(() => {});
  }, [isLoggedIn, currentUser, provider.id]);

  const handleFollow = async () => {
    if (!isLoggedIn) return;
    setFollowLoading(true);
    const wasFollowing = isFollowing;
    try {
      await toggleFollow(provider.id);
      setFollowCount((c) => wasFollowing ? c - 1 : c + 1);
    } finally {
      setFollowLoading(false);
    }
  };

  const coverImage = provider.cover_image_url ||
    `https://public.readdy.ai/ai/img_res/02d26613697b2fcd35f6de4451bde620.jpg${provider.id.slice(0, 8)}&orientation=landscape`;
  const avatarImage = provider.avatar_url ||
    `https://readdy.ai/api/search-image?query=Japanese%20craftsman%20artisan%20professional%20portrait%20warm%20neutral%20background%20soft%20natural%20light%20skilled%20expert%20calm%20confident%20expression&width=200&height=200&seq=avatar-${provider.id.slice(0, 8)}&orientation=squarish`;

  return (
    <div className="relative">
      {/* Cover image */}
      <div className="relative h-72 md:h-96 w-full overflow-hidden">
        <img
          src={coverImage}
          alt={provider.service_name ?? provider.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-deep-green/80 via-deep-green/20 to-transparent" />
        {/* Plan badge */}
        <div className="absolute top-4 right-4">
          <span className={`text-xs px-3 py-1 rounded-full font-sans font-medium ${provider.plan === "light" ? "bg-gold text-white" : "bg-white/80 text-charcoal"}`}>
            {provider.plan === "light" ? "ライトプラン" : "フリープラン"}
          </span>
        </div>
        {/* Back link */}
        <div className="absolute top-4 left-4">
          <Link
            to="/providers"
            className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-sans hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            事業者一覧
          </Link>
        </div>
      </div>

      {/* Provider info bar */}
      <div className="bg-white border-b border-stone-100">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-5">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            {/* Avatar + name */}
            <div className="flex items-end gap-4 -mt-12 md:-mt-14 relative z-10">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border-4 border-white flex-shrink-0">
                <img src={avatarImage} alt={provider.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="pb-1">
                <h1 className="font-serif text-deep-green text-xl md:text-2xl font-medium leading-snug">
                  {provider.service_name || provider.name}
                </h1>
                <p className="text-charcoal/60 text-sm font-sans mt-0.5">
                  {provider.name}
                  {provider.location && ` · ${provider.location}`}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-5 md:ml-auto">
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-gold text-base">★</span>
                <strong className="text-charcoal font-sans">
                  {provider.rating ? provider.rating.toFixed(1) : "—"}
                </strong>
                <span className="text-charcoal/50 font-sans text-xs">
                  ({provider.review_count ?? 0}件)
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-charcoal/60 font-sans">
                <i className="ri-user-follow-line" />
                <span>
                  <strong className="text-charcoal">{followCount.toLocaleString()}</strong> フォロワー
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-charcoal/60 font-sans">
                <i className="ri-play-circle-line" />
                <span>
                  <strong className="text-charcoal">{provider.products?.length ?? 0}</strong> 作品
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 md:ml-4 flex-wrap">
              <button
                onClick={() => setFavored(!favored)}
                className={`w-10 h-10 rounded-full flex items-center justify-center border cursor-pointer transition-all duration-300 flex-shrink-0 ${favored ? "bg-gold/10 border-gold text-gold" : "border-stone-200 text-charcoal/40 hover:border-gold hover:text-gold"}`}
                title="お気に入り"
              >
                <i className={favored ? "ri-heart-fill" : "ri-heart-line"} />
              </button>

              {isLoggedIn ? (
                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={`px-6 py-2.5 rounded-full text-sm font-sans font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${isFollowing ? "bg-teal text-white" : "border border-teal text-teal hover:bg-teal hover:text-white"} ${followLoading ? "opacity-60" : ""}`}
                >
                  {followLoading ? (
                    <span className="flex items-center gap-1.5">
                      <i className="ri-loader-4-line animate-spin" /> 処理中...
                    </span>
                  ) : isFollowing ? "✓ フォロー中" : "+ フォローする"}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="px-6 py-2.5 rounded-full text-sm font-sans font-medium whitespace-nowrap cursor-pointer border border-teal text-teal hover:bg-teal hover:text-white transition-all duration-300"
                >
                  + フォローする
                </Link>
              )}

              <Link
                to={`/messages`}
                className="px-5 py-2.5 rounded-full text-sm font-sans font-medium whitespace-nowrap cursor-pointer bg-deep-green text-white hover:bg-mid-green transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  <i className="ri-message-3-line" />
                  メッセージ
                </span>
              </Link>
            </div>
          </div>

          {/* Slogan */}
          {provider.slogan && (
            <p className="font-display italic text-teal/70 text-sm mt-3 ml-0.5">
              &ldquo;{provider.slogan}&rdquo;
            </p>
          )}

          {/* Tags */}
          {provider.skills && provider.skills.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {provider.skills.map((s) => (
                <span key={s} className="tag-pill text-xs">{s}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
